import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import withApiData from '../HOC/withApiData'
import colors from '../../configuration/colors'
import { hideModal } from '../../actions/modal'
import Modal from '../mini/Modal'
import Button from '../mini/Buttons/Button'
import styled from 'styled-components'
import H2 from '../typography/headers/H2'
import MarginRight15 from '../typography/margins/MarginRight15'
import CancelButton from '../mini/Buttons/CancelButton'
import { editMentorFile, uploadMentorFile } from '../mycourses/Activities/API'
import TextInputComponent from './CreateOrEditCourseModal/InputComponents/TextInputComponent'
import FlexAlignCenter from '../presentational/FlexAlignCenter'
import _EditIcon from '../../static/images/mycourses/Edit.svg'

const ModalContainer = styled.div`
  width: 660px;
  border-radius: 10px;
  box-shadow: 0 2px 3.8px 0.2px rgba(0, 0, 0, 0.15);
  background-color: ${colors.smoke};
  margin: 180px auto;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 10px 30px 30px;
`

const EditIcon = styled.div`
  background-image: url(${_EditIcon});
  height: 18px;
  width: 18px;
  margin-right: 11px;
  background-repeat: no-repeat;
`

const Label = styled.label`
  min-width: 75px;
  display: inline-block;
  border: 1px solid ${colors.aluminum};
  background: ${colors.white};
  color: ${colors.black};
  border-radius: 100px;
  padding: 12px 22px;
  margin-bottom: 10px;
  &:hover {
    color: ${colors.white};
    background: ${colors.charcoal2};
    border: 1px solid ${colors.charcoal2};
  }
`

const FileName = styled.p`
  color: ${colors.black};
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 20px;
`

const AddOrEditMentorFile = ({
  apiData,
  levelId,
  activityQuestId,
  mentorFile,
  isEditing
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const [file, setFile] = useState(
    isEditing ? mentorFile.original_file_name : null
  )
  const [description, setDescription] = useState(
    isEditing ? mentorFile.description : null
  )
  const [isUploadingNote, setIsUploadingNote] = useState(false)

  const handleDescriptionChange = e => {
    setDescription(e.target.value)
  }

  const handleCancel = () => {
    setFile('')
    setDescription('')
    setTimeout(() => {
      dispatch(hideModal())
    })
  }

  const handleFileUpload = e => {
    setFile(e.target.files[0])
  }

  const createMutationUploadNewFile = useMutation(
    ({ apiData, levelId, activityQuestId, file, description }) =>
      uploadMentorFile(apiData, levelId, activityQuestId, file, description)
  )

  const createMutationOnEditDescription = useMutation(
    ({ apiData, levelId, activityQuestId, description, mentorFileId }) =>
      editMentorFile(
        apiData,
        levelId,
        activityQuestId,
        description,
        mentorFileId
      )
  )
  const mentorFileId = isEditing && mentorFile.id
  const handleSaveMentorFile = () => {
    setIsUploadingNote(true)
    if (isEditing) {
      createMutationOnEditDescription.mutate(
        {
          apiData,
          levelId,
          activityQuestId,
          description,
          mentorFileId
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('activity-' + activityQuestId)
            queryClient.invalidateQueries('mentorFiles-' + activityQuestId)
            setIsUploadingNote(false)
            setTimeout(() => {
              dispatch(hideModal())
            }, 300)
          }
        }
      )
    } else {
      createMutationUploadNewFile.mutate(
        {
          apiData,
          levelId,
          activityQuestId,
          file,
          description
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('activity-' + activityQuestId)
            queryClient.invalidateQueries('mentorFiles-' + activityQuestId)
            setTimeout(() => {
              dispatch(hideModal())
            }, 300)
          }
        }
      )
    }
  }

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        {isUploadingNote ? (
          <div className="loading" />
        ) : (
          <React.Fragment>
            <HeaderContainer>
              {isEditing ? (
                <FlexAlignCenter>
                  <EditIcon />
                  <H2 style={{ color: colors.victoria }}>Edit Mentor File</H2>
                </FlexAlignCenter>
              ) : (
                <H2 style={{ color: colors.victoria }}>+ Add Mentor File</H2>
              )}
              <FlexContainer>
                <MarginRight15>
                  <CancelButton
                    handleClick={handleCancel}
                    title="Cancel"
                    width="104px"
                  />
                </MarginRight15>
                <Button
                  width="104px"
                  title="Save"
                  handleClick={handleSaveMentorFile}
                  buttonColor={colors.oeYellow}
                  borderColor={colors.oeYellow}
                  hoverBackground={colors.charcoal4}
                  hoverColor={colors.white}
                  couldSaveData={file !== ''}
                  shouldShowTooltipAndDisableButton={file !== ''}
                />
              </FlexContainer>
            </HeaderContainer>
            <MainContainer>
              <div style={{ textAlign: 'left' }}>
                <div style={{ paddingTop: '15px' }}>
                  {isEditing ? (
                    <div>
                      <FileName>{file}</FileName>
                    </div>
                  ) : (
                    <div className="file">
                      <FlexContainer
                        style={{
                          justifyContent: 'flex-start',
                          alignItems: 'center'
                        }}
                      >
                        <Label htmlFor="mentorNoteFileInput">Choose File</Label>
                        <p style={{ marginLeft: '10px' }}>
                          {file !== null ? file.name : 'No file chosen'}
                        </p>
                        <input
                          id="mentorNoteFileInput"
                          className="activity-icon-input"
                          type="file"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                      </FlexContainer>
                    </div>
                  )}
                  <TextInputComponent
                    title="File Description"
                    placeholder="File Description"
                    handleEnteredTextValue={handleDescriptionChange}
                    value={description}
                    shouldDisplayAsteriskSymbol={false}
                    shouldBeTextarea={false}
                  />
                </div>
              </div>
            </MainContainer>
          </React.Fragment>
        )}
      </ModalContainer>
    </Modal>
  )
}

AddOrEditMentorFile.propTypes = {
  apiData: PropTypes.object.isRequired,
  levelId: PropTypes.number.isRequired,
  activityQuestId: PropTypes.number.isRequired,
  mentorFile: PropTypes.object.isRequired,
  isEditing: PropTypes.bool.isRequired
}

export default withApiData(AddOrEditMentorFile)



// WEBPACK FOOTER //
// ./src/components/modal/AddOrEditMentorFile.js